import { useState, useEffect, useCallback, useRef } from 'react';
import api, { isOfflineShapedError } from '../lib/api';
import { useNetworkStatus } from './useNetworkStatus';
import { shouldRefetchOnRefocus } from './refocusThrottle';
import { readScreenCache, fetchScreenData } from '../lib/screenDataCache';
import { isExplicitlyOffline } from '../lib/offline/networkVerdict';
import { log as diagLog } from '../lib/diag/readyChainLog';

const EMPTY_SUMMARY = { groups: [], total_pending_parts: 0, max_pending: 0, plan: 'free' };

// PEND-1 — EL RELOJ DEL ÚLTIMO RESUMEN BUENO, y solo para el visor.
//
// `readScreenCache` devuelve { data, fresh } y NO expone el `at` de la entrada;
// este encargo tiene prohibido tocar `screenDataCache`, así que la edad de la
// semilla se mide aquí. Es un espejo honesto y no una segunda fuente de verdad:
// el ÚNICO escritor de la llave 'pending-summary' en toda la app es el
// `fetchScreenData` de abajo, así que este sello es el de esa misma escritura.
// Solo se reporta cuando hay semilla; si la entrada se purgó (frontera de
// identidad, invalidación), no hay nada que medir y el campo va en null.
let ultimoResumenBuenoAt = 0;

/**
 * Shared "Pending to process" summary (GET /documents/pending/summary).
 *
 * Consumed by the sidebar badge (group count), the dashboard reactivation banner
 * (any processable group) and the /pending page (the full list). Refreshes on mount,
 * whenever a `studybuddy:pending-changed` window event fires (after any pending mutation),
 * and — throttled to 1 per REFOCUS_THROTTLE_MS — when the tab regains focus/visibility
 * (catches DB-side changes like a credit reset). No timers, no periodic polling: a tab
 * that stays focused triggers zero extra fetches.
 *
 * F4.3b: el resumen vive en screenDataCache bajo 'pending-summary'. Con cache
 * fresco un consumidor que monta NO fetchea (antes: sidebar + banner + página
 * = 3 GETs por navegación; ahora 1 por ventana de frescura, y los fetches
 * concurrentes de varios consumidores se dedupean a uno). El evento
 * pending-changed invalida la key (ver screenDataCache) y cada consumidor
 * montado refresca en vivo — reacción a un hecho, no polling.
 *
 * F6.8 EH-4(a): gana un cuarto disparador —el veredicto del vigía volviendo a
 * UP— y, sobre todo, deja de mentir cuando la lectura no sale: ver `refresh`.
 *
 * PEND-1 D1 (Carlos, 2026-08-06): sin señal, una semilla VENCIDA ya no se tira
 * — se pinta como última foto conocida, bajo la nota de sin conexión y sin
 * autorizar el vacío. Es una excepción firmada a la regla de frescura de
 * `screenDataCache`; el porqué está entero junto a la semilla, abajo.
 *
 * PAGER-1 — `activo` (default TRUE, o sea: nadie cambia si no lo pasa).
 * Con `activo:false` esta INSTANCIA queda dormida: no fetchea al montar, no
 * escucha foco/`visibilitychange`/`pending-changed`, no reacciona a NET.UP, no
 * arranca el poll de `pollWhileProcessing` y no emite `SCR.STALE`/`SCR.EMPTY`
 * —cuyo contrato ("una pantalla…") no aplica a una pantalla que nadie ve, y
 * cuyo `edadSemillaMs` mediría siempre ~0 desde un pre-montaje—. Sigue pintando
 * lo que la SEMILLA ya tenga en memoria: leer local y pintar es lo único que la
 * doctrina le permite a una raíz dormida. Las instancias del CHASIS (badge del
 * nav, Sidebar) y la página `/pending` no pasan nada y quedan intactas; la del
 * Panel sigue la activación de su raíz.
 */
export function usePendingSummary({ pollWhileProcessing = false, activo = true } = {}) {
  const [seed] = useState(() => readScreenCache('pending-summary'));
  const seedFresh = Boolean(seed?.fresh);
  // PEND-1 D1 — SIN SEÑAL, LO ÚLTIMO QUE EL USUARIO VIO, EN VEZ DEL VACÍO.
  //
  // EXCEPCIÓN FIRMADA A UNA REGLA ESCRITA, no un descuido. `screenDataCache.js`
  // (líneas 3-12) prohíbe pintar dato vencido —"PROHIBIDO el stale-then-swap…
  // una sola regla de frescura"— y esto estrena la única salvedad:
  //
  //   · QUIÉN LA FIRMA: Carlos, 2026-08-06.
  //   · POR QUÉ: sin red no se pueden subir documentos, así que NO PUEDEN NACER
  //     pendientes nuevos. La foto no envejece por ese lado, y por eso va SIN
  //     TOPE de antigüedad: cero constantes de caducidad nuevas.
  //   · POR QUÉ NO ES UN SWAP SILENCIOSO: la nota de sin conexión
  //     (`PendingPage.js`, bloque `pending-offline-notice`) va ENCIMA y declara
  //     que lo de abajo puede estar viejo. Lo que la regla prohíbe es el dato
  //     viejo que se hace pasar por fresco y luego brinca; aquí va anunciado.
  //   · HASTA DÓNDE LLEGA: SOLO `data`. `hasSummary` se queda en FALSE — una
  //     foto vieja no es una respuesta exitosa del servidor y no puede autorizar
  //     "No tienes pendientes", que es la mentira que mató EH-4(a). Si la foto
  //     viene vacía, lo que se pinta es la nota sola, igual que hoy.
  //   · CON RED NO CAMBIA NADA: semilla vencida y señal viva sigue pidiendo al
  //     servidor y mostrando el esqueleto, exactamente como antes.
  //
  // Se decide UNA vez, en el montaje —por eso vive en `useState` y no en una
  // variable de render—: el veredicto puede cambiar después, y esta foto no.
  // `isExplicitlyOffline()` es dictamen, jamás sospecha (networkVerdict.js).
  const [seedStaleOffline] = useState(
    () => Boolean(seed && !seed.fresh && isExplicitlyOffline())
  );
  const [data, setData] = useState(seedFresh || seedStaleOffline ? seed.data : EMPTY_SUMMARY);
  // Sin red no hay nada que esperar: el esqueleto sería una espera sin final.
  const [loading, setLoading] = useState(!seedFresh && !seedStaleOffline);
  // F6.8 EH-4(a) — ¿LO DE ARRIBA ES UN RESUMEN, O EL VACÍO DE ARRANQUE?
  // `EMPTY_SUMMARY` se usaba para las dos cosas y ahí nacía la mentira: un fallo
  // de red lo escribía encima y la vista pintaba "No tienes pendientes", que es
  // una afirmación sobre LA CUENTA. Este bit separa "el servidor dijo que no hay"
  // de "todavía no sé". El badge cuelga del mismo dato, así que dice lo mismo.
  const [hasSummary, setHasSummary] = useState(seedFresh);
  // null | 'offline' | 'error' — la frontera EH-2, con las mismas dos mitades.
  const [problem, setProblem] = useState(null);
  // El seed cuenta como fetch para el throttle de refocus (no re-fetchear de
  // inmediato lo que acabamos de pintar fresco).
  const lastFetchRef = useRef(seedFresh ? Date.now() : 0);
  // El veredicto por el hook ÚNICO: cero oídos nuevos de `window`, cero consultas
  // nuevas al sensor. Viaja en un ref para que `refresh` siga siendo estable (lo
  // tienen agarrado tres listeners y un intervalo).
  const net = useNetworkStatus();
  const offlineRef = useRef(net.isOffline);
  offlineRef.current = net.isOffline;

  const refresh = useCallback(async () => {
    // Con DICTAMEN de sin señal la petición está condenada: no se quema radio
    // (coherente con la T5) y lo último conocido se queda donde está.
    if (offlineRef.current) {
      setProblem('offline');
      setLoading(false);
      return;
    }
    lastFetchRef.current = Date.now();
    try {
      const res = await fetchScreenData('pending-summary', async () => {
        const r = await api.get('/documents/pending/summary');
        return r.data || EMPTY_SUMMARY;
      });
      setData(res);
      setHasSummary(true);
      setProblem(null);
      ultimoResumenBuenoAt = Date.now();
      // PEND-1 — el contrato de SCR.PAINT, intacto: SOLO dato fresco del
      // servidor. La foto vieja y el vacío tienen códigos propios (abajo).
      diagLog('SCR.PAINT', { screen: 'pendientes', grupos: (res?.groups || []).length });
    } catch (err) {
      // UN ERROR JAMÁS BORRA LO QUE YA SE SABÍA. Antes escribía `EMPTY_SUMMARY`
      // encima, y con eso el badge se apagaba y la vista decía "No tienes
      // pendientes" — dos mentiras por una lectura que no salió. Ahora sólo se
      // anota QUÉ pasó; quién lo pinta decide arriba.
      setProblem(isOfflineShapedError(err) ? 'offline' : 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // La señal VOLVIÓ (dictamen del vigía) → el resumen se re-pide solo, por el
  // MISMO bus al que este hook ya está suscrito. Sin esto, un consumidor montado
  // se quedaba con la nota de sin conexión hasta que el usuario cambiara de
  // pestaña (el refoco era su único despertador). El dedupe de screenDataCache
  // hace que esto y el refresco de montaje sean UNA petición, no dos.
  useEffect(() => {
    if (!activo) return;              // PAGER-1: NET.UP es red — espera activación.
    if (!net.isOnline) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [net.isOnline, activo]);

  useEffect(() => {
    if (!activo) return undefined;    // PAGER-1: ni fetch de montaje ni oídos.
    // Cache fresco → sin fetch de montaje (regla F4.3b); viejo/vacío → fetch.
    if (!seedFresh) refresh();
    // Explicit mutations refresh immediately (no throttle).
    const onChanged = () => refresh();
    // Tab refocus / becomes visible: refresh only if throttle window has elapsed.
    const onRefocus = () => {
      if (shouldRefetchOnRefocus(Date.now(), lastFetchRef.current, document.visibilityState)) {
        refresh();
      }
    };
    window.addEventListener('studybuddy:pending-changed', onChanged);
    window.addEventListener('focus', onRefocus);
    document.addEventListener('visibilitychange', onRefocus);
    return () => {
      window.removeEventListener('studybuddy:pending-changed', onChanged);
      window.removeEventListener('focus', onRefocus);
      document.removeEventListener('visibilitychange', onRefocus);
    };
  }, [refresh, seedFresh, activo]);

  // R1: BOUNDED polling — only when explicitly requested (the /pending page) and ONLY
  // while some group still has parts processing. The interval cleans itself up the
  // moment processing reaches 0 (effect dependency flips) — never permanent polling.
  const hasProcessingParts = (data.groups || []).some(g => (g.processing_count || 0) > 0);
  useEffect(() => {
    if (!activo) return undefined;    // PAGER-1: un poll es un reloj — espera activación.
    if (!pollWhileProcessing || !hasProcessingParts) return undefined;
    const id = setInterval(refresh, 6000);
    return () => clearInterval(id);
  }, [pollWhileProcessing, hasProcessingParts, refresh, activo]);

  // PEND-1 — LA LÍNEA DE ARRANQUE PARA EL VISOR: Pendientes deja de ser muda.
  //
  // Va en el HOOK y no en la página a propósito: así el badge del sidebar y el
  // de "More" —que son OTRAS instancias de esto mismo— quedan cubiertos con la
  // misma línea. UNA por ACTIVACIÓN (PAGER-1: antes era "por montaje", con
  // dependencia vacía; con `activo` fijo en true son lo mismo), nunca por
  // render. `diagLog` es no-op TOTAL en web, jamás lanza, no estrena timers y
  // no altera tiempos (contratos de readyChainLog.js).
  useEffect(() => {
    // PAGER-1: las sondas del visor hablan de LO QUE EL USUARIO VE. Dormida, la
    // instancia no emite (y `edadSemillaMs` no reporta un ~0 de pre-montaje).
    if (!activo) return;
    if (seedFresh) return;   // pintó la semilla fresca: ni vacío ni foto vieja
    const edadSemillaMs = seed && ultimoResumenBuenoAt ? Date.now() - ultimoResumenBuenoAt : null;
    if (seedStaleOffline) {
      diagLog('SCR.STALE', {
        screen: 'pendientes', grupos: (seed.data?.groups || []).length, edadSemillaMs,
      });
      return;
    }
    diagLog('SCR.EMPTY', {
      screen: 'pendientes',
      motivo: seed ? 'semilla-vencida' : 'sin-semilla',
      edadSemillaMs,
      teniaSemilla: Boolean(seed),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activo]);

  const groups = data.groups || [];
  const processableGroups = groups.filter(g => g.processable);
  return {
    groups,
    loading,
    refresh,
    // EH-4(a) — la distinción, para que cada superficie diga la verdad:
    //   offline    → no hay señal (veredicto vivo o petición que no llegó);
    //   loadFailed → hubo señal y el servidor falló;
    //   hasSummary → detrás de `groups` hay una respuesta EXITOSA del servidor
    //                (lo único que autoriza a decir "no tienes pendientes").
    //
    // El veredicto entra AQUÍ y no sólo por el `problem`: así el estado voltea al
    // instante al perder la señal (EH-1), sin esperar a que algo intente leer. Y
    // manda sobre el `problem`, igual que en las otras tres pantallas: un 5xx
    // viejo no puede decir "el servidor falló" cuando ahora ya no hay señal.
    offline: net.isOffline || problem === 'offline',
    loadFailed: problem === 'error' && !net.isOffline,
    hasSummary,
    groupCount: groups.length,
    // Pending DOCUMENTS (groups) the user can continue right now — the banner's unit.
    processableGroupCount: processableGroups.length,
    // Parts the user can process right now across all processable groups.
    processableCount: processableGroups.reduce((acc, g) => acc + (g.affordability?.k || 0), 0),
    hasProcessable: processableGroups.length > 0,
    totalPending: data.total_pending_parts || 0,
    maxPending: data.max_pending || 0,
    plan: data.plan || 'free',
    // Balance snapshot (credits / doc spaces / Pending storage) for the page's chips.
    snapshot: data.snapshot || null,
    // post-t7-1 (H3): los avisos sin ver de documentos enteros guardados en Pendientes (llave nueva del resumen;
    // un servidor viejo no la manda y esto es una lista vacia)
    avisosDocumentoEntero: data.avisos_documento_entero || SIN_AVISOS,
    isOverTope: (data.total_pending_parts || 0) > (data.max_pending || 0),
  };
}

const SIN_AVISOS = [];

// Convenience: fire after any pending mutation so all consumers refresh.
export function notifyPendingChanged() {
  window.dispatchEvent(new CustomEvent('studybuddy:pending-changed'));
}
